import {gql} from '@apollo/client';
import Backbone from 'backbone';
import {DocumentNode} from 'graphql';

import * as statusbar from 'components/StatusBar';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {FileStateAttributes} from 'filestate/FileState';
import FolderItemState from 'filestate/FolderItemState';
import ApolloFolderItemBase from 'folder-items/ApolloFolderItemBase';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import QueryWrapper from 'graphql-client/QueryWrapper';
import Preferences from 'Preferences';

import {InaccessibleInfoGraphQL} from '../../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {
  JellyfishFileStateQuery,
  JellyfishFileStateQueryVariables,
} from './__generated/JellyfishFileStateQuery';
import JellyfishTabState from './JellyfishTabState';

type JellyfishFileStateGraphQL = ExtractByTypename<JellyfishFileStateQuery['jellyfish'], 'JellyfishNode'>;

export const JELLYFISH_FILE_STATE_QUERY: DocumentNode = gql`
  query JellyfishFileStateQuery($fileId: StrictString!) @skipAudit @engComponent(name: CORE_OBJECT) {
    jellyfish(id: $fileId) {
      ...BaseFolderItemFileGraphQL
      ...JellyfishFileStateGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('JellyfishFileState').fragment}
  ${getFragmentWrapper('BaseFolderItemFile').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export class Jellyfish extends ApolloFolderItemBase<
  JellyfishFileStateQuery,
  JellyfishFileStateGraphQL,
  JellyfishFileStateQueryVariables
> {
  protected getQueryWrapper(): QueryWrapper<JellyfishFileStateQuery, JellyfishFileStateQueryVariables> {
    return {query: JELLYFISH_FILE_STATE_QUERY};
  }

  protected queryVariables(): JellyfishFileStateQueryVariables {
    return {fileId: this.id};
  }

  protected getGraphQLFolderItemFromQueryData(
    data: JellyfishFileStateQuery
  ): JellyfishFileStateGraphQL | InaccessibleInfoGraphQL {
    return data.jellyfish;
  }

  protected getUpdatedQueryData(attrs: Partial<JellyfishFileStateGraphQL>): JellyfishFileStateQuery {
    return {
      jellyfish: {
        ...this.folderItem(),
        ...attrs,
      },
    };
  }
}

interface JellyfishFileState extends FolderItemState {
  model(): Jellyfish;
}

class JellyfishFileState extends FolderItemState {
  static override Model = Jellyfish;
  static override fileType = ModelTypeConstants.JELLYFISH;

  static override initClass(): void {
    this.prototype.hasNote = false;
  }

  override defaults(): FileStateAttributes {
    return {
      ...super.defaults(),
      toolbar: new Backbone.Model(),
      statusbar: new statusbar.StatusBarState(),
    };
  }

  override hasMetadata(): boolean {
    return true;
  }

  override initialize(attributes: {}, options: unknown): void {
    super.initialize(attributes, options);
    const jellyfishTab = new JellyfishTabState({id: this.id});
    this.buffers().at(0).tabs().add(jellyfishTab);
    this.buffers().at(0).tabs().add(this.metadataTab);
    jellyfishTab.set({active: true});
    const splitPref = Preferences.get(this.getSplitWorkspacePreferenceKey());
    this.setIsWorkspaceSplit(splitPref != null ? splitPref : false);
  }
}

JellyfishFileState.initClass();

export default JellyfishFileState;
